import {Dialog, DialogFooter, Form, FormButton} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isNotNil} from 'ramda';

import type {Nullish} from 'shared/web';

import {t} from '../utils/catalogueUi';
import {priceInput} from '../utils/moneyInput';
import {GrossPriceField} from './GrossPriceField';

const samePriceSchema = z.object({priceNet: priceInput});
type SamePriceValues = z.infer<typeof samePriceSchema>;

type SamePriceDialogProps = {
  itemName: string | Nullish;
  hint: string;
  startPrice: string | Nullish;
  currency: string;
  vatRate: string | Nullish;
  onApply: (priceNet: string) => Promise<unknown>;
  onClose: () => void;
};

/** "Same price on all trims" for one item; open while `itemName` is set. */
export function SamePriceDialog(props: SamePriceDialogProps) {
  return (
    <Dialog
      isOpen={isNotNil(props.itemName)}
      onClose={props.onClose}
      withAdditionalFooter
      title={t(
        'entity.newVehicle.catalogue.samePriceOnAllTrimsOf',
        'Same price on all trims · {feature}',
        {feature: props.itemName ?? ''}
      )}
    >
      <Show when={isNotNil(props.itemName)}>
        <Form<SamePriceValues>
          experimentalZodSchema={samePriceSchema}
          defaultValues={{priceNet: props.startPrice ?? ''}}
          onSubmit={(values) => props.onApply(values.priceNet)}
        >
          {(control) => (
            <VStack spacing={4}>
              <Text size="small" color="secondary">
                {props.hint}
              </Text>
              <GrossPriceField
                control={control}
                name="priceNet"
                vatRate={props.vatRate}
                suffix={props.currency}
                label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
                isRequired
              />
              <DialogFooter>
                <HStack justify="flex-end">
                  <FormButton
                    control={control}
                    type="submit"
                    title={t('general.actions.apply', 'Apply')}
                  />
                </HStack>
              </DialogFooter>
            </VStack>
          )}
        </Form>
      </Show>
    </Dialog>
  );
}
